import { IRequiredEntity } from "@gtmhub/datasources/models/models";
import { IInsightFormatting, IInsight, IInsightMetricUsage, IMarketplaceInsightTemplate } from "@gtmhub/insights";
import { IParameter } from "@gtmhub/parameters/models";
import { IAccess } from "@gtmhub/sessions";
import { ITag } from "@gtmhub/tags";
import { IInsightCalculationData } from "@gtmhub/sockets";
import { IDynamicValue } from "@gtmhub/shared/components/grid-filtering-bar";

export interface IInsightboard {
  id?: string;
  name: string;
  tag: string;
  layout?: string;
  deleting?: boolean;
  deleted?: boolean;
  notificationsCount?: number;
  usersCount?: number;
  appsCount?: number;
  insightBoardItems?: IInsightboardItem[];
  insights?: IInsightboardItem[];
  sectionHeaders?: IInsightboardItem[];
  tags?: ITag[];
  settings?: IInsightboardItemSetting[];
  access: IAccess;
  parameters?: IParameter[];
  currentUserAllowedActions?: string[];
  readOnly?: boolean;
  isReport?: boolean;
  // ui stuff
  allowedActions?: {
    [action: string]: boolean;
  };
}

export interface IMarketplaceInsightboardTemplate {
  id: string;
  name: string;
  description: string;
  tags: ITag[];
  featured: boolean;
  installsCount: number;
  insightTemplates: IMarketplaceInsightTemplate[];
  createdById: string;
  previewUrl: string;
  teaserUrl: string;
}

interface IInstalledInsightboards {
  id: string;
  name: string;
}

export interface IInstalledInsightboardTemplatesResponse {
  createdInsightIds: string[];
  installedInsightboards: IInstalledInsightboards[];
}

export interface IInsightboardItem {
  id?: string;
  dashboardId: string;
  sizeX: number;
  sizeY: number;
  row?: number;
  col?: number;
  insightName?: string;
  insightTitle?: string;
  insightId?: string;
  sectionHeaderId?: string;
  type: "insight" | "sectionHeader";
  settings?: IInsightboardItemSetting[];
  filters?: string[];
  insightRequiredEntities?: IRequiredEntity[];
  insightSource?: IInsightSource;
  insightState?: IInsightState;
  formatting?: IInsightFormatting;
  sectionHeader?: ISectionHeader;
}

export interface ISectionHeader {
  id?: string;
  header: string;
  subHeader: string;
}

export interface IInsightBoardItemResponse {
  sectionHeaders: IInsightboardItem[];
  insights: IInsightboardItem[];
}

export interface IInsightboardItemView extends IInsightboardItem {
  shouldHightlight?: boolean;
  scrollIntoView?: boolean;
}

export interface IInsightSource {
  originalVersion: string;
  sourceApplication: string;
}

export interface IInsightState {
  error?: string;
  metadata: IInsightStateMetadata;
  status: string;
}

export interface IInsightStateMetadata {
  connectorName: string;
  count: number;
  datasourceId: string;
  names: string;
  type: string;
}

export interface IInsightboardItemSetting {
  name: string;
  value: string;
  valueDisplayName?: string;
  dynamicValues?: IDynamicValue[];
}

export enum IInsightboardItemSettingName {
  session = "session",
  team = "team",
}

export interface IInsightboardPermissions {
  form: boolean;
  modifyName: boolean;
  modifyPermissions: boolean;
}

export interface IInsightboardsParams {
  tags: {
    [tagName: string]: string;
  };
}

export interface IInsightboardWidgets {
  array: IInsightboardItem[];
  map: {
    [insightboardItemId: string]: IInsightboardItem;
  };
}

export interface IInsightboardTransactions {
  insightId: string;
  transactionId: string;
}

export interface IInsightsIdMap {
  [insightId: string]: IInsightDetails;
}

export interface IInsightDetails {
  insight: IInsight;
  calculationResult?: IInsightCalculationData;
  commentsCount?: number;
  metrics?: IInsightMetricUsage[];
  // ui stuff
  loading?: boolean;
  connected?: number;
  source?: IInsightSource;
  insightRequiredEntities?: IRequiredEntity[];
}

export interface IInsightboardFiltersUsage {
  [parameterKey: string]: string[];
}

export interface IRemovedInsightboardParametersMap {
  [parameterKey: string]: boolean;
}

export interface IReportFilter {
  name: string;
  id?: string;
  dateCreated?: string;
  accountId?: string;
  ownerId?: string;
  filterType: string;
  dashboardItemsSettings: IInsightboardItemSetting[];
}

export enum InsightEditorType {
  codelessEditor = "codelessEditor",
  sqlExpertEditor = "sqlExpertEditor",
}

export interface IInsightboardSettingsChange {
  settings: IInsightboardItemSetting[];
  settingsChanged: boolean;
}

export interface ICachedInsightResponse {
  insightId: string;
  data: IInsightCalculationData;
}

export interface ICachedInsightIdsResponse {
  ids: string[];
}

export interface IInsightTemplateSuggestionSettings {
  dismissed?: boolean;
  tooltipShown: boolean;
  insightboards: IInsightTemplateSuggestionSetting[];
}

export interface IInsightTemplateSuggestionSetting {
  insightboardId: string;
  numberOfSuggestionsToShow: number;
  linkToMarketplaceDismissed: boolean;
}
export interface IDirectSubmitInsightboard {
  name: string;
  description: string;
  previewUrl: string;
  tags?: ITag[];
  insights: IDirectSubmitInsightboardItem[];
}

export interface IDirectSubmitInsightboardItem {
  insightId: string;
  insightTitle: string;
  insightDescription: string;
  width: number;
  height: number;
  row: number;
  col: number;
  hidden: boolean;
  showInList: boolean;
}
